import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import AppShell from '../components/AppShell';
import { useUnits } from '../lib/units';
import { apiFetch } from '../lib/api';
import { fmtAccounting, fmtDate, fmtDateTime } from '../lib/format';
import type { BalanceResponse, StatementResponse, StatementLine } from '../lib/types';

// Dashboard layout:
//   [ Balance card .................... ] [ Quick actions ]
//   [ Recent transactions table .................................. ]
// The balance card carries the heroic typography — the moment the page loads
// the eye lands on the running balance.
export default function Dashboard() {
    const { t, i18n } = useTranslation();
    const { active, activeCntId } = useUnits();

    const [bal,    setBal]    = useState<BalanceResponse | null>(null);
    const [recent, setRecent] = useState<StatementLine[]>([]);
    const [loading, setLoading] = useState(false);
    const [err,     setErr]     = useState<string | null>(null);

    useEffect(() => {
        if (!activeCntId) return;
        let cancelled = false;
        setLoading(true); setErr(null);
        (async () => {
            try {
                const [balResp, stmt] = await Promise.all([
                    apiFetch<BalanceResponse>(`/api/account/balance?cntId=${activeCntId}`),
                    apiFetch<StatementResponse>(`/api/account/statement?cntId=${activeCntId}&preset=all`),
                ]);
                if (cancelled) return;
                setBal(balResp);
                setRecent(stmt.lines.slice(-5).reverse());
            } catch (e) {
                if (!cancelled) setErr((e as Error).message);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [activeCntId]);

    return (
        <AppShell>
            <div className="max-w-[1280px] mx-auto px-6 sm:px-12 py-12">
                {!activeCntId ? (
                    <div className="text-ink-muted">{t('units.selectFirst')}</div>
                ) : (
                    <div className="space-y-10">
                        <div className="grid lg:grid-cols-12 gap-6 stagger">
                            <BalanceCard
                                balance={bal}
                                loading={loading}
                                unitLabel={active ? `${active.bldNm} — ${active.untNm}` : ''}
                            />
                            <QuickActions />
                        </div>

                        <RecentTable
                            rows={recent}
                            loading={loading}
                            multiCurrency={bal?.multiCurrency ?? false}
                            err={err}
                            lng={i18n.language}
                        />
                    </div>
                )}
            </div>
        </AppShell>
    );
}

// ---------- Balance card -------------------------------------------------

function BalanceCard({ balance, loading, unitLabel }: {
    balance: BalanceResponse | null; loading: boolean; unitLabel: string;
}) {
    const { t, i18n } = useTranslation();
    if (loading && !balance) {
        return (
            <section className="lg:col-span-8 card relative overflow-hidden">
                <div className="eyebrow text-ember">{t('dashboard.balanceHeading')}</div>
                <div className="mt-6 h-16 w-2/3 bg-slate-fog rounded animate-pulse" />
                <div className="mt-4 h-3 w-1/3 bg-slate-fog rounded animate-pulse" />
            </section>
        );
    }
    if (!balance) return <section className="lg:col-span-8 card" />;

    return (
        <section className="lg:col-span-8 card relative overflow-hidden">
            {/* corner ember mark */}
            <span aria-hidden="true" className="absolute top-0 start-0 w-12 h-px bg-ember" />
            <span aria-hidden="true" className="absolute top-0 start-0 w-px h-12 bg-ember" />

            <div className="flex items-baseline justify-between">
                <p className="eyebrow text-ember">{t('dashboard.balanceHeading')}</p>
                <p className="text-[11px] text-ink-dim font-body tabular-nums">
                    {t('dashboard.asOf', { when: fmtDateTime(balance.asOf, i18n.language) })}
                </p>
            </div>

            <p className="font-body text-ink-muted text-sm mt-2 truncate">{unitLabel}</p>

            {balance.multiCurrency ? (
                <div className="mt-6 space-y-3">
                    <p className="font-display text-ember text-2xl">{t('dashboard.multiCurrency')}</p>
                    <div className="grid sm:grid-cols-3 gap-px bg-slate-line border border-slate-line rounded-sm overflow-hidden">
                        {balance.balances.map((b) => (
                            <div key={b.crncID ?? b.currency ?? '—'} className="bg-slate-raised p-4">
                                <p className="eyebrow text-ink-dim">{b.currency || '—'}</p>
                                <p className="balance-number text-ink-on text-3xl mt-1 accounting">
                                    {fmtAccounting(b.balance)}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            ) : (
                <div className="mt-8 flex items-baseline gap-4 flex-wrap">
                    <p className="balance-number text-ink-on text-balance accounting"
                       style={{ fontSize: 'clamp(3rem, 7vw, 5.5rem)' }}>
                        {fmtAccounting(balance.currentBalance ?? 0)}
                    </p>
                    <p className="font-display text-ember text-2xl">{balance.currency || '—'}</p>
                </div>
            )}

            {balance.sharedAccount && (
                <p className="mt-6 text-sm text-ink-muted italic">
                    {t('dashboard.sharedAccount', { n: balance.sharedAccount.covers })}
                </p>
            )}
        </section>
    );
}

// ---------- Quick actions ------------------------------------------------

function QuickActions() {
    const { t } = useTranslation();
    return (
        <section className="lg:col-span-4 grid gap-3">
            <Link to="/app/statement"
                  className="card hover:border-ember/40 hover:bg-slate-fog transition-colors group flex flex-col justify-between min-h-[120px]">
                <p className="eyebrow text-ember">→ {t('nav.statement')}</p>
                <p className="font-display text-ink-on text-xl mt-2 group-hover:translate-x-1 transition-transform">
                    {t('dashboard.viewStatement')}
                </p>
            </Link>
            <Link to="/app/upload"
                  className="card hover:border-ember/40 hover:bg-slate-fog transition-colors group flex flex-col justify-between min-h-[120px]">
                <p className="eyebrow text-ember">→ {t('nav.upload')}</p>
                <p className="font-display text-ink-on text-xl mt-2 group-hover:translate-x-1 transition-transform">
                    {t('dashboard.uploadProof')}
                </p>
            </Link>
        </section>
    );
}

// ---------- Recent table -------------------------------------------------

function RecentTable({ rows, loading, multiCurrency, err, lng }: {
    rows: StatementLine[]; loading: boolean; multiCurrency: boolean; err: string | null; lng: string;
}) {
    const { t } = useTranslation();
    return (
        <section className="card animate-fade-up">
            <div className="flex items-baseline justify-between mb-6">
                <h2 className="font-display text-ink-on text-2xl tracking-tight">{t('dashboard.recent')}</h2>
                <Link to="/app/statement" className="btn-link text-sm">
                    {t('dashboard.viewStatement')} →
                </Link>
            </div>
            <div className="rule-fade -mx-7 mb-2" />

            {err && <p className="text-ember-deep py-6">{err}</p>}

            {!err && !loading && rows.length === 0 && (
                <p className="text-ink-muted py-6 text-center italic">{t('dashboard.noActivity')}</p>
            )}

            {rows.length > 0 && (
                <div className="overflow-x-auto -mx-7 px-7">
                    <table className="min-w-full">
                        <thead>
                            <tr className="text-start text-[10px] uppercase tracking-widest2 text-ink-dim">
                                <th className="py-3 text-start font-medium">{t('statement.col_date')}</th>
                                <th className="py-3 text-start font-medium">{t('statement.col_desc')}</th>
                                <th className="py-3 text-end font-medium">{t('statement.col_dbt')}</th>
                                <th className="py-3 text-end font-medium">{t('statement.col_cdt')}</th>
                                <th className="py-3 text-end font-medium">{t('statement.col_balance')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r, i) => (
                                <tr key={`${r.leID}-${r.ledID}`}
                                    className={`border-t border-slate-line/60 ${i % 2 === 1 ? 'bg-slate-fog/30' : ''}`}>
                                    <td className="py-3 pr-4 text-ink-on font-body text-sm tabular-nums whitespace-nowrap">
                                        {fmtDate(r.dt, lng)}
                                    </td>
                                    <td className="py-3 pr-4 text-ink-on text-sm font-body max-w-[480px] truncate">{r.dsc || '—'}</td>
                                    <td className="py-3 pr-4 text-end text-ink-muted text-sm accounting">{r.dbt ? r.dbt.toLocaleString(undefined, { maximumFractionDigits: 2 }) : ''}</td>
                                    <td className="py-3 pr-4 text-end text-ink-muted text-sm accounting">{r.cdt ? r.cdt.toLocaleString(undefined, { maximumFractionDigits: 2 }) : ''}</td>
                                    <td className="py-3 text-end text-ink-on text-sm accounting">
                                        {fmtAccounting(r.balance)}
                                        {multiCurrency && r.currency && <span className="text-ink-dim text-[10px] ms-2">{r.currency}</span>}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </section>
    );
}
